import { useCallback, useEffect, useRef, useState } from "react";

import { colocaToast } from "@/shared/utils/toast";

const COPIED_FOR_MS = 2000;

export interface UseCopyToClipboard {
  /** The value copied in the last two seconds, so the button that copied it can say so. */
  copiedValue: string | null;
  copy: (value: string, toastMessage?: string) => Promise<boolean>;
}

export function useCopyToClipboard(errorMessage?: string): UseCopyToClipboard {
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (value: string, toastMessage?: string) => {
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        if (errorMessage) colocaToast.error(errorMessage);
        return false;
      }
      setCopiedValue(value);
      if (toastMessage) colocaToast.success(toastMessage);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopiedValue(null), COPIED_FOR_MS);
      return true;
    },
    [errorMessage],
  );

  return { copiedValue, copy };
}
